/**
 * useSubscription Hook
 * 
 * Real-time subscription status from Firestore.
 * Connects to Stripe via Cloud Functions.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts';
import {
  SubscriptionData,
  startCheckout,
  subscribeToStatus,
  hasPremiumAccess,
  getTrialDaysRemaining,
  openCustomerPortal,
} from '@/services/subscription';

interface UseSubscriptionReturn {
  // State
  isPremium: boolean;
  isTrialing: boolean;
  isCanceling: boolean;
  trialDaysRemaining: number | null;
  hasUsedTrial: boolean;
  isLoading: boolean;
  stripeCustomerId: string | null;
  currentPeriodEnd: number | null;
  // Free-tier Price Check counter
  priceChecksUsed: number;
  priceChecksLimit: number;

  // Actions
  subscribe: () => Promise<void>;
  openPortal: () => Promise<void>;
}

const PRICE_CHECKS_LIMIT = 3;

const DEFAULT_DATA: SubscriptionData = {
  premiumStatus: 'free',
};

export function useSubscription(): UseSubscriptionReturn {
  const { user, isAuthenticated } = useAuth();
  const [data, setData] = useState<SubscriptionData>(DEFAULT_DATA);
  const [isLoading, setIsLoading] = useState(true);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!isAuthenticated || !user?.uid) {
      setData(DEFAULT_DATA);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    
    const unsubscribe = subscribeToStatus(user.uid, (newData) => {
      setData(newData);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [isAuthenticated, user?.uid]);

  // Start Stripe checkout
  const subscribe = useCallback(async () => {
    try {
      await startCheckout();
    } catch (error) {
      console.error('Failed to start checkout:', error);
      throw error;
    }
  }, []);

  // Open Stripe Customer Portal
  const openPortal = useCallback(async () => {
    if (!data.stripeCustomerId) {
      throw new Error('No Stripe customer found. Subscribe to continue.');
    }

    try {
      await openCustomerPortal();
    } catch (error) {
      console.error('Failed to open portal:', error);
      throw error;
    }
  }, [data.stripeCustomerId]);

  // Computed values
  const isPremium = hasPremiumAccess(data);
  const isTrialing = data.premiumStatus === 'trial';
  const isCanceling = data.premiumStatus === 'canceling';
  const trialDaysRemaining = getTrialDaysRemaining(data);
  const hasUsedTrial = data.hasUsedTrial ?? false;
  const stripeCustomerId = data.stripeCustomerId ?? null;
  const currentPeriodEnd = data.currentPeriodEnd ?? null;
  const priceChecksUsed = data.priceChecksUsed ?? 0;

  return {
    isPremium,
    isTrialing,
    isCanceling,
    trialDaysRemaining,
    hasUsedTrial,
    isLoading,
    stripeCustomerId,
    currentPeriodEnd,
    priceChecksUsed,
    priceChecksLimit: PRICE_CHECKS_LIMIT,
    subscribe,
    openPortal,
  };
}

export default useSubscription;